import { Node } from "./canvas-utils";
import { ActiveTarget } from "./AutomationConstructor";
import { Button, MenuItem, Select, TextField, Autocomplete, FormControl, InputLabel } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import _ from "lodash";
import { FaTrash } from "react-icons/fa";

const VARIABLES = [
  {
    label: "Номер поезда",
    value: "trainNumber",
  },
  {
    label: "Номер вагона",
    value: "wagonNumber",
  },
  {
    label: "Номер места",
    value: "seatNumber",
  },
  {
    label: "Дата поездки",
    value: "departureDate",
  },
  {
    label: "Текст сообщения",
    value: "content",
  },
  {
    label: "Тематика обращения",
    value: "categoryID",
  }
];
const ACTION_TYPES = [
  {
    label: "Добавить тег",
    value: "ADD_TAG",
    schema: {
      fields: [
        {
          key: "tags",
          label: "Теги",
          type: "string[]",
          required: true,
        },
      ],
    },
  },
  {
    label: "Удалить тег",
    value: "REMOVE_TAG",
    schema: {
      fields: [
        {
          key: "tags",
          label: "Теги",
          type: "string[]",
          required: true,
        },
      ],
    },
  },
  {
    label: "Уведомление в Telegram",
    value: "TELEGRAM_NOTIFICATION",
    schema: {
      fields: [
        {
          key: "message",
          label: "Сообщение",
          type: "string",
          required: true,
        },
      ],
    },
  },
  {
    label: "Создать сделку в CRM",
    value: "CREATE_LEAD",
    schema: {
      fields: [
        {
          key: "name",
          label: "Префикс в названия сделки",
          type: "string",
          required: true,
        },
      ],
    },
  },
  {
    label: "Создать жалобу в CRM",
    value: "CREATE_TICKET",
    schema: {
      fields: [
        {
          key: "variables",
          label: "Переменные",
          type: "autocomplete",
          options: VARIABLES,
          multiple: true,
          required: true,
        },
      ]
    },
  },
  {
    label: "Сбор данных контакта",
    value: "COLLECT_DATA",
    schema: {
      fields: [
        {
          key: "variableType",
          label: "Выберите тип данных",
          type: "select",
          required: true,
          options: [
            {
              label: "Текст",
              value: "text",
            },
            {
              label: "Email",
              value: "email",
            },
            {
              label: "Телефон",
              value: "phone",
            },
          ]
        },
        {
          key: "variableName",
          label: "Сохранить ответ в переменную",
          type: "select",
          required: true,
          options: VARIABLES,
        },
      ],
    },
  },
  // Устновить переменную в CRM
  {
    label: "Устновить переменную в CRM",
    value: "SET_VARIABLE",
    schema: {
      fields: [
        {
          key: "variableName",
          label: "Сохранить ответ в переменную",
          type: "select",
          required: true,
          options: VARIABLES,
        },
        {
          key: "variableValue",
          label: "Значение переменной",
          type: "string",
          required: true,
        },
      ],
    },
  },
];

export const actionTypesMap = _.keyBy(ACTION_TYPES, "value");

interface ActionEditFormProps {
  target: ActiveTarget | null;
  onSave: (node: Node) => void;
}

const ActionEditForm = ({ target, onSave }: ActionEditFormProps) => {
  const { register, handleSubmit, watch, control } = useForm({
    defaultValues: {
      type: _.get(target, "entity.data.type", ""),
      payload: _.get(target, "entity.data.payload", {}),
    },
    mode: "onChange",
  });

  const onSubmit = (formInput: any) => {
    console.log("formInput", formInput);
    onSave({
      ...(target?.entity as Node),
      data: {
        type: formInput.type,
        payload: formInput.payload,
      },
    });
  };

  const onDelete = () => {
    if (window.confirm("Вы уверены, что хотите удалить действие?")) {
      onSave({
        ...(target?.entity as Node),
        data: {
          type: "",
          payload: {},
        },
      });
    }
  };

  return (
    <div>
      <form className="flex flex-col gap-4">
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel>Тип действия</InputLabel>
              <Select {...field} label="Тип действия">
                {ACTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        {watch("type") && (
          <div className="flex flex-col gap-4">
            {ACTION_TYPES.find((type) => type.value === watch("type"))?.schema?.fields?.map(
              (field: any) => {
                if (field.type === "string") {
                  return (
                    <div key={field.key}>
                      <TextField
                        fullWidth
                        label={field.label}
                        {...register(`payload.${field.key}`)}
                      />
                    </div>
                  );
                }
                if (field.type === "number") {
                  return (
                    <div key={field.key}>
                      <TextField label={field.label} {...register(`payload.${field.key}`)} />
                    </div>
                  );
                }
                if (field.type === "boolean") {
                  return (
                    <div key={field.key}>
                      <TextField label={field.label} {...register(`payload.${field.key}`)} />
                    </div>
                  );
                }
                if (field.type === "string[]") {
                  return (
                    <div key={field.key} id={field.key}>
                      <Controller
                        control={control}
                        name={`payload.${field.key}`}
                        defaultValue={[]}
                        render={({ field: f }) => (
                          <Autocomplete
                            value={f.value || []}
                            onChange={(_, value) => f.onChange(value)}
                            multiple
                            freeSolo
                            options={[]} // Можно оставить пустым, если подсказки не нужны
                            renderInput={(params) => (
                              <TextField {...params} id={field.key} label={field.label} />
                            )}
                          />
                        )}
                      />
                    </div>
                  );
                }

                if (field.type === "select") {
                  return (
                    <div key={field.key}>
                      <Controller control={control} name={`payload.${field.key}`} render={({ field: f }) => (
                        <FormControl fullWidth>
                          <InputLabel>{field.label}</InputLabel>
                          <Select {...f} label={field.label}>
                            {field?.options?.map((option: any) => (
                              <MenuItem key={option?.value} value={option?.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )} />
                    </div>
                  )
                }

                if (field.type === "autocomplete") {
                  return (
                    <div key={field.key}>
                      <Controller
                        {...field}
                        control={control}
                        name={`payload.${field.key}`}
                        render={({ field: f }) => {
                          let value = f.value ? field.options?.find((o: any) => o.value === f.value) : null
                          if (field.multiple) {
                            if (f.value) {
                              value = field.options?.filter((o: any) => f.value.includes(o.value))
                            } else {
                              value = []
                            }
                          }
                          console.log("value:", {
                            value,
                            multiple: field.multiple,
                          });

                          return (
                            <Autocomplete
                              value={value}
                              onChange={(_, value) => {
                                console.log("value:", value);
                                if (field.multiple) {
                                  f.onChange(value ? value.map((o: any) => o.value) : null)
                                } else {
                                  f.onChange(value ? value.id : null)
                                }
                              }}
                              multiple={field.multiple}
                              getOptionLabel={(option) => option.label}
                              options={field.options || []}
                              renderInput={(params) => (
                                <TextField {...params} id={field.key} label={field.label} />
                              )}
                            />
                          )
                        }}
                      />
                    </div>
                  );
                }
              }
            )}
          </div>
        )
        }

        <div className="mt-8 flex gap-2">
          <Button variant="contained" type="button" fullWidth onClick={handleSubmit(onSubmit)}>
            Сохранить
          </Button>
          <Button variant="outlined" type="button" color="error" onClick={() => onDelete()}>
            <FaTrash />
          </Button>
        </div>
        <pre>{JSON.stringify(watch(), null, 2)}</pre>
      </form>
    </div>
  );
};

export default ActionEditForm;
